/**
 * JSON-LD Schema Generators
 * Generates structured data for various page types
 */

import { SITE_CONFIG } from './config';
import { getProfessionSlug, getVacancySlug } from './urlBuilder';

/**
 * Generate Organization schema
 */
export const generateOrganizationSchema = () => ({
  '@context': 'https://schema.org',
  '@type': 'Organization',
  '@id': `${SITE_CONFIG.url}/#organization`,
  name: SITE_CONFIG.name,
  alternateName: SITE_CONFIG.alternateName,
  url: SITE_CONFIG.url,
  logo: {
    '@type': 'ImageObject',
    url: SITE_CONFIG.logo,
    width: 512,
    height: 512
  },
  email: SITE_CONFIG.email,
  foundingDate: SITE_CONFIG.foundingDate,
  sameAs: SITE_CONFIG.socialProfiles,
  description: SITE_CONFIG.description
});

/**
 * Generate WebSite schema
 */
export const generateWebSiteSchema = (searchUrl) => ({
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  '@id': `${SITE_CONFIG.url}/#website`,
  url: SITE_CONFIG.url,
  name: SITE_CONFIG.name,
  description: SITE_CONFIG.description,
  publisher: {
    '@id': `${SITE_CONFIG.url}/#organization`
  },
  potentialAction: searchUrl ? {
    '@type': 'SearchAction',
    target: {
      '@type': 'EntryPoint',
      urlTemplate: `${SITE_CONFIG.url}${searchUrl}?search={search_term_string}`
    },
    'query-input': 'required name=search_term_string'
  } : undefined,
  inLanguage: 'uk-UA'
});

/**
 * Generate BreadcrumbList schema
 */
export const generateBreadcrumbSchema = (breadcrumbs, baseUrl) => {
  if (!breadcrumbs || breadcrumbs.length === 0) return null;

  const items = breadcrumbs.map((crumb, index) => {
    const listItem = {
      '@type': 'ListItem',
      position: index + 1,
      name: crumb.label || crumb.name || 'Page'
    };
    
    // Only add item property if link exists (required for all items except last)
    // Google requires either "name" or "item.name" - we always provide "name"
    if (crumb.link) {
      listItem.item = `${baseUrl}${crumb.link}`;
    }
    
    return listItem;
  });

  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items
  };
};

/**
 * Build a Schema.org Person author from a populated author entity.
 * Returns null if name cannot be derived.
 */
const buildPersonAuthor = (author, baseUrl) => {
  if (!author) return null;
  const name = author.nameUA || author.nameEN;
  if (!name) return null;

  const sameAs = Array.isArray(author.socialLinks)
    ? author.socialLinks
        .map((link) => (typeof link?.url === 'string' ? link.url.trim() : ''))
        .filter((url) => url.length > 0)
    : [];

  const person = { '@type': 'Person', name };
  if (author.slug) person['@id'] = `${baseUrl}/authors/${author.slug}#person`;
  if (author.picture) person.image = author.picture;
  const jobTitle = author.jobTitleUA || author.jobTitleEN;
  if (jobTitle) person.jobTitle = jobTitle;
  const description = author.bioUA || author.bioEN;
  if (description) person.description = description;
  const knowsAbout = author.expertiseUA || author.expertiseEN;
  if (knowsAbout) person.knowsAbout = knowsAbout;
  if (sameAs.length > 0) person.sameAs = sameAs;

  return person;
};

/**
 * Generate Article schema
 */
export const generateArticleSchema = (article, baseUrl, authorName) => {
  if (!article) return null;

  const personAuthor = buildPersonAuthor(article.author, baseUrl);

  return {
    '@context': 'https://schema.org',
    '@type': 'Article',
    '@id': `${baseUrl}/articles/${article.slug}/${article._id}#article`,
    headline: article.title,
    description: article.description || article.meta?.description,
    image: article.postImage ? `${article.postImage}/w1200h627` : `${baseUrl}${SITE_CONFIG.defaultImage}`,
    datePublished: article.publishedAt || article.createdAt,
    dateModified: article.updatedAt || article.publishedAt || article.createdAt,
    author: personAuthor || {
      '@type': 'Person',
      name: authorName || article.authorName || article.authorNameUA || SITE_CONFIG.name
    },
    publisher: {
      '@id': `${SITE_CONFIG.url}/#organization`
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `${baseUrl}/articles/${article.slug}/${article._id}`
    },
    wordCount: article.wordCount,
    articleSection: article.category?.name
  };
};

/**
 * Generate Occupation/Profession schema
 */
export const generateOccupationSchema = (profession, baseUrl) => {
  if (!profession) return null;

  const slug = getProfessionSlug(profession);
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Occupation',
    '@id': `${baseUrl}/professions/${slug}/${profession._id}#occupation`,
    name: profession.name,
    description: profession.descriptionShort || profession.description,
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `${baseUrl}/professions/${slug}/${profession._id}`
    }
  };

  // Add salary information if available
  if (profession.salary?.min || profession.salary?.max) {
    schema.estimatedSalary = {
      '@type': 'MonetaryAmountDistribution',
      name: 'Salary',
      currency: 'UAH',
      minValue: profession.salary?.min,
      maxValue: profession.salary?.max,
      unitText: 'MONTH'
    };
  }

  // Add skills if available
  if (profession.skills && profession.skills.length > 0) {
    schema.skills = profession.skills.map(skill => skill.name || skill);
  }

  // Add responsibilities if available
  if (profession.responsibilities && profession.responsibilities.length > 0) {
    schema.responsibilities = profession.responsibilities.join(', ');
  }

  // Add education requirements if available
  if (profession.educationRequirements) {
    schema.educationRequirements = profession.educationRequirements;
  }

  // Add experience requirements if available
  if (profession.experienceRequirements) {
    schema.experienceRequirements = profession.experienceRequirements;
  }

  return schema;
};

/**
 * Generate JobPosting schema for vacancies
 */
export const generateJobPostingSchema = (vacancy, baseUrl) => {
  if (!vacancy) return null;

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'JobPosting',
    '@id': `${baseUrl}/vacancies/${getVacancySlug(vacancy)}/${vacancy._id}#jobposting`,
    title: vacancy.title || vacancy.name,
    description: vacancy.description,
    datePosted: vacancy.createdAt || vacancy.publishedAt,
    validThrough: vacancy.validThrough || new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
    employmentType: vacancy.type?.toUpperCase() || 'FULL_TIME',
    hiringOrganization: {
      '@type': 'Organization',
      name: vacancy.company?.name || vacancy.companyName || SITE_CONFIG.name,
      sameAs: vacancy.company?.website,
      logo: vacancy.company?.logo
    },
    jobLocation: vacancy.location ? {
      '@type': 'Place',
      address: {
        '@type': 'PostalAddress',
        addressLocality: vacancy.location,
        addressCountry: 'UA'
      }
    } : {
      '@type': 'Place',
      address: {
        '@type': 'PostalAddress',
        addressCountry: 'UA'
      }
    },
    applicantLocationRequirements: vacancy.remote ? {
      '@type': 'Country',
      name: 'Remote'
    } : undefined
  };

  // Add salary if available
  if (vacancy.salary?.min || vacancy.salary?.max) {
    schema.baseSalary = {
      '@type': 'MonetaryAmount',
      currency: vacancy.salary?.currency || 'UAH',
      value: {
        '@type': 'QuantitativeValue',
        minValue: vacancy.salary?.min,
        maxValue: vacancy.salary?.max,
        unitText: 'MONTH'
      }
    };
  }

  return schema;
};

/**
 * Generate Course schema
 */
export const generateCourseSchema = (course, baseUrl, platformName) => {
  if (!course) return null;

  return {
    '@context': 'https://schema.org',
    '@type': 'Course',
    '@id': `${baseUrl}/education/courses/${course.slug}/${course._id}#course`,
    name: course.name || course.title,
    description: course.description,
    provider: {
      '@type': 'Organization',
      name: platformName || course.platform?.name || SITE_CONFIG.name,
      sameAs: course.platform?.website
    },
    hasCourseInstance: {
      '@type': 'CourseInstance',
      courseMode: course.format === 'online' ? 'online' : 'onsite',
      duration: course.duration ? `P${course.duration}D` : undefined
    },
    offers: course.price ? {
      '@type': 'Offer',
      price: course.price,
      priceCurrency: 'UAH',
      availability: 'https://schema.org/InStock'
    } : undefined,
    educationalLevel: course.level,
    inLanguage: course.language || 'uk'
  };
};

/**
 * Generate Person schema for consultants/mentors
 */
export const generatePersonSchema = (person, type, baseUrl) => {
  if (!person) return null;

  return {
    '@context': 'https://schema.org',
    '@type': 'Person',
    '@id': `${baseUrl}/${type}s/${person.slug}/${person._id}#person`,
    name: person.name,
    description: person.description || person.bio,
    image: person.photo || person.avatar,
    jobTitle: type === 'consultant' ? 'Career Consultant' : 'Mentor',
    worksFor: {
      '@id': `${SITE_CONFIG.url}/#organization`
    },
    sameAs: [
      person.linkedin,
      person.twitter,
      person.website
    ].filter(Boolean),
    knowsAbout: person.specializations || person.skills
  };
};

/**
 * Generate FAQPage schema
 */
export const generateFAQSchema = (questions) => {
  if (!questions || questions.length === 0) return null;

  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: questions.map(q => ({
      '@type': 'Question',
      name: q.question || q.title,
      acceptedAnswer: {
        '@type': 'Answer',
        text: q.answer || q.content
      }
    }))
  };
};

/**
 * Generate ItemList schema for list pages
 */
export const generateItemListSchema = (items, itemType, baseUrl, path) => {
  if (!items || items.length === 0) return null;

  return {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    itemListElement: items.slice(0, 10).map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      url: `${baseUrl}${path}/${item.slug}/${item._id}`,
      name: item.name || item.title
    }))
  };
};

/**
 * Generate CollectionPage schema
 */
export const generateCollectionPageSchema = (title, description, baseUrl, path) => ({
  '@context': 'https://schema.org',
  '@type': 'CollectionPage',
  '@id': `${baseUrl}${path}#webpage`,
  url: `${baseUrl}${path}`,
  name: title,
  description: description,
  isPartOf: {
    '@id': `${SITE_CONFIG.url}/#website`
  },
  about: {
    '@id': `${SITE_CONFIG.url}/#organization`
  },
  inLanguage: 'uk-UA'
});

/**
 * Generate EducationalOrganization schema
 */
export const generateEducationalOrgSchema = (org, baseUrl) => {
  if (!org) return null;

  return {
    '@context': 'https://schema.org',
    '@type': 'EducationalOrganization',
    '@id': `${baseUrl}/education/${org.slug}/${org._id}#organization`,
    name: org.name,
    description: org.description,
    url: org.website,
    logo: org.logo,
    address: org.address ? {
      '@type': 'PostalAddress',
      streetAddress: org.address.street,
      addressLocality: org.address.city,
      addressCountry: org.address.country || 'UA'
    } : undefined,
    contactPoint: org.phone || org.email ? {
      '@type': 'ContactPoint',
      telephone: org.phone,
      email: org.email,
      contactType: 'admissions'
    } : undefined
  };
};

const schemaGenerators = {
  generateOrganizationSchema,
  generateWebSiteSchema,
  generateBreadcrumbSchema,
  generateArticleSchema,
  generateOccupationSchema,
  generateJobPostingSchema,
  generateCourseSchema,
  generatePersonSchema,
  generateFAQSchema,
  generateItemListSchema,
  generateCollectionPageSchema,
  generateEducationalOrgSchema
};

export default schemaGenerators;

